/**
 * Step: Product Type
 *
 * Shown ONLY when the tenant has more than one registration-selectable
 * product template. The user picks which product type they are creating a
 * registration for; the choice (`state.productTemplateUuid`) then drives the
 * rest of the wizard - the host/property step's early attributes (e.g. the
 * contractor picker), warranty narrowing, etc.
 *
 * Mirrors the claims wizard's "claim type" selection step: a grid of cards,
 * one per selectable template, where clicking a card selects it and advances.
 * When a tenant has a single selectable template this step is never inserted
 * into the segment list and the template is resolved implicitly.
 */

import { Package } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'

export interface ProductTypeOption {
  uuid: string
  name: string
  host_requirement?: string
  product_count?: number
}

function templateDescription(t: ProductTypeOption): string {
  if (t.host_requirement === 'REQUIRED' || t.host_requirement === 'OPTIONAL') {
    return 'Includes property and installation details'
  }
  if (typeof t.product_count === 'number') {
    return `${t.product_count} product${t.product_count === 1 ? '' : 's'}`
  }
  return 'Product registration'
}

export function StepProductType({
  templates,
  selectedUuid,
  onSelect,
}: {
  templates: ProductTypeOption[]
  selectedUuid: string | null
  onSelect: (uuid: string) => void
}) {
  return (
    <div>
      <p className="mb-5 text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
        Select a product type
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {templates.map((t) => {
          const isSelected = selectedUuid === t.uuid
          return (
            <Button
              key={t.uuid}
              type="button"
              variant="ghost"
              onClick={() => onSelect(t.uuid)}
              className={cn(
                'group relative flex h-auto w-full min-w-0 shrink flex-col items-start gap-4 overflow-hidden whitespace-normal rounded-[8px] border border-solid p-5 text-left transition-all duration-150',
                isSelected
                  ? 'border-[var(--green)] bg-[var(--green-bg)]'
                  : 'cursor-pointer border-[var(--rule-strong)] bg-[var(--bg-subtle)] hover:-translate-y-0.5 hover:border-[var(--ink-3)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)]',
              )}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[6px] bg-[var(--green-bg)] text-[var(--green)]">
                <Package size={18} />
              </span>
              <div className="min-w-0 w-full">
                <p className="mb-1 text-[13px] font-normal leading-snug break-words whitespace-normal uppercase tracking-[0.08em] text-[var(--ink)]">
                  {t.name}
                </p>
                <p className="text-[12px] font-light leading-[1.4] text-[var(--ink-3)]">
                  {templateDescription(t)}
                </p>
              </div>
            </Button>
          )
        })}
      </div>
    </div>
  )
}
